import { IPromise, IQService, IScope } from "angular";
import { StateService, UIRouterGlobals } from "@uirouter/angularjs";
import { Observable, take, takeUntil } from "rxjs";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { AccountType } from "@gtmhub/core";
import { IStateInit } from "@gtmhub/core/routing";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { ApmSpanService } from "@gtmhub/core/tracing/apm-span.service";
import { CustomFieldActions } from "@gtmhub/customFields/redux/custom-field-actions";
import { ICustomFieldsStoreState } from "@gtmhub/customFields/redux/custom-field-reducer";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import {
  generateDefaultFilters,
  generateDefaultFiltersWithoutSession,
  goalsFilterDefinitions,
  goalsGridPersistedScreenPath,
  goalsPersistedScreenPath,
  goalsViewOptionsPath,
} from "@gtmhub/goals/filter-utils";
import { localize } from "@gtmhub/localization";
import { OKR_VIEW_SCREEN_PATH, expandedGoalsOkrViewScreenPath } from "@gtmhub/okrs/okr-views/utils/okr-view-utils";
import { BASE_OKR_VIEW_STATE } from "@gtmhub/okrs/okrs.states";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import {
  GridFilterDefinitions,
  GridSortingDefinitions,
  IArrayGridFilter,
  IGoalsSharedViewOptions,
  IGridFilter,
  IGridFiltersChange,
  IGridSortingRule,
  OrFiltersGroup,
  getPersistedScreenSettings,
  setPersistedScreenSettings,
  toGridDefinitionMap,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { SimplePermissionsAccessType } from "@gtmhub/shared/components/simple-permissions/simple-permissions.component";
import { determineAccessType } from "@gtmhub/shared/components/simple-permissions/simple-permissions.utils";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { Ng1DestroyService } from "@gtmhub/shared/services";
import { INgRedux } from "@gtmhub/state-management";
import { AccountResolverService } from "@gtmhub/state/account-resolver-service";
import { CurrentUserService } from "@gtmhub/users/current-user.service";
import { toIdMap } from "@gtmhub/util";
import { Assignee } from "@webapp/assignees/models/assignee.models";
import { ICustomFieldsAllTypesMap } from "@webapp/custom-fields/models/custom-fields.models";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { NavigationEvents } from "@webapp/navigation/components/navigation/navigation.events";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { OkrViewEventsManager } from "@webapp/okrs/okr-views/utils/okr-view.events";
import { ISessionsIdMap, Session } from "@webapp/sessions/models/sessions.model";
import { SaveFilterChangesContent } from "@webapp/shared/unified-filters/components/save-filter-changes/save-filter-changes.component";
import { ShareableFilter, ShareableFilterBasePostDTO, ShareableFilterTransientPostDTO } from "@webapp/shared/unified-filters/models/shareable-filters.models";
import { ShareableFiltersModalsService } from "@webapp/shared/unified-filters/services/shareable-filters-modals.service";
import { ShareableFiltersFacade } from "@webapp/shared/unified-filters/services/shareable-filters/shareable-filters-facade.service";
import { IIndicator } from "../../../error-handling/models";
import { ISessionNode, MISSING_SESSION_ID_STATE_PARAM } from "../../../goals/models";
import { constructSessionsTree } from "../../../goals/utils";
import { AlignmentContentState, AlignmentLoader, AlignmentTreeViewMode, IAlignmentLoadedEventArgs } from "../alignment-loader";
import { arrayFilterIncludes, resetPersistedExpandedItems } from "../utils";

const alignmentScreenName = "alignment";
const nestedScreenName = "nested";

export type AlignmentViewModes = Record<"nested" | "showMetrics", boolean>;

interface IAlignmentSidebarRedux {
  customFields: ICustomFieldsAllTypesMap;
  assigneesIdMap: Record<string, Assignee>;
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
  sessions: Session[];
}

interface IAlignmentSidebarScopeBindings {
  filtersUpdated(filters: OrFiltersGroup[]): void;
}

export interface IAlignmentSidebarScope extends IExtendedRootScopeService, IAlignmentSidebarRedux, IScope, IAlignmentSidebarScopeBindings {
  isOkrFiltersImprovementEnabled: boolean;
  enabledDrag: boolean;
  indicators: {
    initial?: IIndicator;
    searching?: IIndicator;
  };
  sessionsTree: ISessionNode;
  isDemoAccount: boolean;
  filterDefinitions: GridFilterDefinitions;
  filters: OrFiltersGroup[];
  sortingDefinitions: GridSortingDefinitions;
  sortingRules: IGridSortingRule[];
  viewModes: AlignmentViewModes;
  // we update alignment-sidebar-ctrl(parent) by reference from alignment-tree-ctrl and alignment-nested-ctrl(children) scope:
  // - receive data with args and assign it to a variable object to keep the reference to the parent
  contentState: AlignmentContentState;
  sessions: Session[];
  isShareableLinksWithAppliedFiltersFeatureEnabled: boolean;
  isOkrViewsFeatureEnabled: boolean;
  isTreeLoadedFromOkrView: boolean;
  hasFilterChanged: boolean;
  saveFilterChangesContent: SaveFilterChangesContent;
  sortingChange(): void;
  filtersChange(change: IGridFiltersChange): void;
  collapseAll(): void;
  expandAll(): void;
  changeViewMode(): void;
  toggleIncludeKeyResults(): void;
  switchDragMode(): void;
  copyShareableLinkWithAppliedFilters(): void;
}

export interface IAlignmentSidebarStateParamsService {
  planningSessionId?: string;
  sharedId?: string;
  sharedParentId?: string;
  locateId?: string;
  filterId?: string;
  okrViewId?: string;
}

const bindStateToScope = (state: IAssigneesStoreState & ICustomFieldsStoreState & ISessionsStoreState): IAlignmentSidebarRedux => ({
  assigneesIdMap: state.assignees.map,
  customFields: state.customFields.map,
  reduxItemsFetched: state.assignees.isFetched && state.customFields.isFetched && state.sessions.isFetched,
  reduxItemsError: state.assignees.error || state.customFields.error || state.sessions.error,
  sessions: state.sessions.items,
});

export class AlignmentSidebarCtrl implements IStateInit {
  private sessionIdMap: ISessionsIdMap;
  private currentSessionId: string;
  private sharedFilterSettings: ShareableFilter;
  private okrViewEventsManager: OkrViewEventsManager;
  private destroy$: Observable<unknown>;

  /**
   * In nested view the session filters are read-only and holding only host session id as a value.
   * Filters are shared between OKRs grid, tree and nested screens.
   * In nested view we're replacing the persisted session filters with newly created sessions filter holding only host session id as a value.
   * This map holds the newly created session object as a key, and the persisted filter replaced as a value.
   * When changes are applied to the filters and we have to persist them, we're replacing the created filters with their persisted mappings(if such).
   */
  private replacedPersistedFiltersMap = new Map<IGridFilter, IGridFilter>();

  public static $inject = [
    "$rootScope",
    "$scope",
    "$state",
    "$stateParams",
    "$q",
    "AlignmentLoader",
    "CurrentUserService",
    "AccountResolverService",
    "UIErrorHandlingService",
    "AssigneeActions",
    "CustomFieldActions",
    "$ngRedux",
    "PlanningSessionsActions",
    "ApmSpanService",
    "FeatureTogglesFacade",
    "$uiRouterGlobals",
    "ShareableFiltersFacade",
    "ShareableFiltersModalsService",
  ];

  constructor(
    private $rootScope: ITraceRootScopeService,
    private $scope: IAlignmentSidebarScope,
    private $state: StateService,
    private $stateParams: IAlignmentSidebarStateParamsService,
    private $q: IQService,
    private alignmentLoader: AlignmentLoader,
    private currentUserService: CurrentUserService,
    private accountResolverService: AccountResolverService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private assigneeActions: AssigneeActions,
    private customFieldActions: CustomFieldActions,
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private apmSpanService: ApmSpanService,
    private featureToggleService: FeatureTogglesFacade,
    private $uiRouterGlobals: UIRouterGlobals,
    private shareableFiltersFacade: ShareableFiltersFacade,
    private shareableFiltersModalsService: ShareableFiltersModalsService
  ) {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", () => {
      this.resetPersistedExpandedItems();
      return unsubscribe();
    });
    angular.element(window).on("unload", () => {
      this.resetPersistedExpandedItems();
    });

    this.destroy$ = new Ng1DestroyService(this.$scope);

    this.$scope.collapseAll = this.collapseAll;
    this.$scope.expandAll = this.expandAll;
    this.$scope.switchDragMode = this.switchDragMode;
    this.$scope.filtersChange = this.filtersChange;
    this.$scope.sortingChange = this.sortingChange;
    this.$scope.changeViewMode = this.changeViewMode;
    this.$scope.toggleIncludeKeyResults = this.toggleIncludeKeyResults;
    this.$scope.contentState = { hasItems: false, noFiltersOrHostSessionOnly: true, isMissingRequiredSessionFilters: false };
    this.$scope.indicators = {};

    const account = this.accountResolverService.getAccountData();
    this.$scope.isDemoAccount = account.type === AccountType.DemoAccount;
    this.$scope.copyShareableLinkWithAppliedFilters = this.copyShareableLinkWithAppliedFilters;

    this.alignmentLoader.init();
  }

  public stateInit(): IPromise<unknown> {
    if (this.$stateParams.planningSessionId === MISSING_SESSION_ID_STATE_PARAM) {
      this.$state.go("gtmhub.goals.all", { planningSessionId: this.$stateParams.planningSessionId });
    } else {
      this.$scope.$on(NavigationEvents.SUB_NAVIGATION_ITEM_SHAREABLE_LINK_COPIED_TO_CLIPBOARD, this.handleShareableLinkCopiedFromSubNavigation);
      return this.$q
        .all({
          isOkrFiltersImprovementEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.OkrFiltersImprovement),
          isShareableLinksWithAppliedFiltersFeatureEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.ShareableLinksWithAppliedFilters),
          isOkrViewsFeatureEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.OkrViews),
        })
        .then(({ isOkrFiltersImprovementEnabled, isShareableLinksWithAppliedFiltersFeatureEnabled, isOkrViewsFeatureEnabled }) => {
          this.$scope.isOkrFiltersImprovementEnabled = isOkrFiltersImprovementEnabled;
          this.$scope.isShareableLinksWithAppliedFiltersFeatureEnabled = isShareableLinksWithAppliedFiltersFeatureEnabled;
          this.$scope.isOkrViewsFeatureEnabled = isOkrViewsFeatureEnabled;
          return this.init();
        });
    }
  }

  private init = (): IPromise<unknown> => {
    return this.apmSpanService.dataLoadSpan("loading-assignees-cf-teams-sessions-redux", () => {
      this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
      this.$ngRedux.dispatch(this.customFieldActions.getCustomFields());
      this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());

      this.$scope.indicators.initial = { progress: true };
      this.setDragOption();

      return this.$q
        .all({
          _: this.$scope.watchUntilAndRejectOnError(
            () => this.$scope.reduxItemsFetched,
            () => this.$scope.reduxItemsError
          ),
        })
        .then(
          () => {
            // keeping this assignment before indicators are turned off in order to show/hide corresponding html before revealing it

            delete this.$scope.indicators.initial;

            const sortedSessions = this.$scope.sessions.sort((a: Session, b: Session) => b.end.localeCompare(a.end));
            this.initSessions(sortedSessions);

            this.$scope.isTreeLoadedFromOkrView = this.$uiRouterGlobals.current.name.startsWith(BASE_OKR_VIEW_STATE);
            if (this.$scope.isOkrViewsFeatureEnabled) {
              this.initFilterChangeActionsContent();
            }

            this.setViewModes();
            this.initUnifiedFilters();
          },
          (err) => this.uiErrorHandlingService.handleModal(err)
        );
    });
  };

  private initFilterChangeActionsContent = (): void => {
    this.$scope.saveFilterChangesContent = [
      ...(this.$scope.isTreeLoadedFromOkrView
        ? [
            {
              text: "Save",
              ctaCb: () => this.patchOkrView(),
            },
            {
              text: "or",
            },
            {
              text: "Save as new OKR view",
              ctaCb: () => this.openOkrViewCreationModal(),
            },
          ]
        : [
            {
              text: "You can save this as a custom OKR view",
            },
            {
              text: "Save as OKR view",
              ctaCb: () => this.openOkrViewCreationModal(),
            },
          ]),
    ];
  };

  private generateShareableFilterPayload = (): ShareableFilterBasePostDTO => {
    return {
      filters: this.$scope.filters[0],
      sorting: this.$scope.sortingRules,
    };
  };

  private openOkrViewCreationModal = (): void => {
    const shareableFilter = this.generateShareableFilterPayload();

    this.shareableFiltersModalsService
      .openSaveFilterAsViewModal({ shareableFilter, placeholder: "Enter an OKR View name", hint: "What different users will see depend on their access to sessions." })
      .afterClose.pipe(take(1))
      .subscribe((data) => {
        if (!data) return;

        this.navigateToSingleOkrView(data.id);
      });
  };

  private patchOkrView = (): void => {
    const shareableFilter = this.generateShareableFilterPayload();

    this.shareableFiltersFacade
      .patchShareableFilter$(this.$stateParams.okrViewId, shareableFilter)
      .pipe(take(1))
      .subscribe({
        next: (sharedFilterSettings) => {
          this.$scope.hasFilterChanged = false;
          this.sharedFilterSettings = sharedFilterSettings;
          this.$scope.$evalAsync();
        },
        error: (error) => {
          this.uiErrorHandlingService.handleModal(error);
        },
      });
  };

  private navigateToSingleOkrView = (okrViewId: string): void => {
    this.$state.go("gtmhub.okrView", { okrViewId });
  };

  private constructAlignmentViewArgsFromFilters = (): IAlignmentLoadedEventArgs => {
    return {
      assigneesIdMap: this.$scope.assigneesIdMap,
      sessionIdMap: this.sessionIdMap,
      currentSessionId: this.currentSessionId,
      sharedId: this.alignmentLoader.isResolved ? undefined : this.$stateParams.sharedId,
      sharedParentId: this.alignmentLoader.isResolved ? undefined : this.$stateParams.sharedParentId,
      locateId: this.alignmentLoader.isResolved ? null : this.$stateParams.locateId,
      viewModes: this.$scope.viewModes,
      enabledDrag: this.$scope.enabledDrag,
      order: this.$scope.sortingRules,
      filters: this.$scope.filters,
      contentState: this.$scope.contentState,
    };
  };

  private determineViewMode = (): "nested" | "relaxed" | "medium" => {
    if (this.$scope.viewModes.nested) {
      return "nested";
    } else {
      return this.$scope.viewModes.showMetrics ? "relaxed" : "medium";
    }
  };

  private setViewModes = (): void => {
    const viewModeFromUserSettings: AlignmentTreeViewMode = this.currentUserService.getUserSetting("alignmentTreeMode");
    const gridViewOptions: IGoalsSharedViewOptions = getPersistedScreenSettings(this.getViewOptionsPath(), "sharedViewOptions") || {};

    // TODO: Delete with https://gtmhub.atlassian.net/browse/GVS-6367
    const shouldMigrateSettingsFromUserSettings =
      viewModeFromUserSettings && typeof gridViewOptions.isNestedView === "undefined" && typeof gridViewOptions.includeKeyResults === "undefined";

    if (shouldMigrateSettingsFromUserSettings) {
      switch (viewModeFromUserSettings) {
        case "medium":
        case "compact":
          {
            gridViewOptions.isNestedView = false;
            gridViewOptions.includeKeyResults = false;
          }
          break;
        case "relaxed":
          {
            gridViewOptions.isNestedView = false;
            gridViewOptions.includeKeyResults = true;
          }
          break;
        case "nested":
          {
            gridViewOptions.isNestedView = true;
            gridViewOptions.includeKeyResults = false;
          }
          break;
      }

      setPersistedScreenSettings(this.getViewOptionsPath(), "sharedViewOptions", gridViewOptions);
    }

    this.$scope.viewModes = {
      nested: !!gridViewOptions.isNestedView && !this.$stateParams.locateId && !this.$stateParams.sharedId,
      showMetrics: gridViewOptions.includeKeyResults == null ? true : gridViewOptions.includeKeyResults,
    };
  };

  private getViewOptionsPath = (): string[] => {
    if (this.$scope.isTreeLoadedFromOkrView) {
      return OKR_VIEW_SCREEN_PATH;
    }

    return goalsViewOptionsPath;
  };

  private setDragOption = (): void => {
    const persistedViewOptions: IGoalsSharedViewOptions = getPersistedScreenSettings(this.getViewOptionsPath(), "sharedViewOptions") || {};

    if (persistedViewOptions) {
      this.$scope.enabledDrag = !!persistedViewOptions.useDragMode;
    } else {
      this.$scope.enabledDrag = false;
    }
  };

  private initSessions = (sessions: Session[]): void => {
    this.sessionIdMap = toIdMap(sessions);
    this.currentSessionId = this.$stateParams.planningSessionId;

    this.$scope.sessionsTree = constructSessionsTree(this.currentSessionId, this.sessionIdMap);
  };

  private switchDragMode = (): void => {
    this.$rootScope.traceAction("switch_drag_mode", () => {
      this.$scope.enabledDrag = !this.$scope.enabledDrag;
      const gridViewOptions: IGoalsSharedViewOptions = getPersistedScreenSettings(this.getViewOptionsPath(), "sharedViewOptions") || {};
      gridViewOptions.useDragMode = this.$scope.enabledDrag;
      setPersistedScreenSettings(this.getViewOptionsPath(), "sharedViewOptions", gridViewOptions);
      this.$scope.$broadcast("alignment.dragModeChanged", { enabledDrag: this.$scope.enabledDrag });
    });
  };

  private expandAll = (): void => {
    this.$rootScope.traceAction("expand_all", () => {
      this.$scope.$broadcast("alignment.expandAll");
    });
  };

  private collapseAll = (): void => {
    this.$rootScope.traceAction("collapse_all", () => {
      this.$scope.$broadcast("alignment.collapseAll");
    });
  };

  private changeViewMode = (): void => {
    // selecting current view is disabled in the UI so we can only choose the not currently selected option
    this.$scope.viewModes.nested = !this.$scope.viewModes.nested;
    const gridViewOptions: IGoalsSharedViewOptions = getPersistedScreenSettings(this.getViewOptionsPath(), "sharedViewOptions") || {};
    gridViewOptions.isNestedView = this.$scope.viewModes.nested;
    setPersistedScreenSettings(this.getViewOptionsPath(), "sharedViewOptions", gridViewOptions);

    this.$rootScope.traceAction("switch_view_mode", () => {
      let newState: "gtmhub.goals.alignmentSidebar.nested" | "gtmhub.goals.alignmentSidebar.tree";
      if (this.$scope.viewModes.nested) {
        newState = "gtmhub.goals.alignmentSidebar.nested";
      } else {
        newState = "gtmhub.goals.alignmentSidebar.tree";
      }

      const viewMode = this.determineViewMode();
      this.$scope.$broadcast("alignment.viewModeSwitched", viewMode);

      this.alignmentLoader.init();

      this.$state.go(newState).then(() => {
        this.initUnifiedFilters();
      });
    });
  };

  private toggleIncludeKeyResults = (): void => {
    const gridViewOptions: IGoalsSharedViewOptions = getPersistedScreenSettings(this.getViewOptionsPath(), "sharedViewOptions") || {};
    gridViewOptions.includeKeyResults = this.$scope.viewModes.showMetrics;
    setPersistedScreenSettings(this.getViewOptionsPath(), "sharedViewOptions", gridViewOptions);

    if (this.$scope.viewModes.nested) {
      this.$rootScope.$broadcast("nested.viewModeChanged", { showMetrics: this.$scope.viewModes.showMetrics });
    } else {
      this.$rootScope.traceAction("switch_view_mode", () => {
        const viewMode = this.determineViewMode();
        this.$scope.$broadcast("alignment.viewModeSwitched", viewMode);
      });
    }
  };

  private screenFiltersPath = () => goalsGridPersistedScreenPath(this.$stateParams.planningSessionId);

  private screenSortingPath = () => goalsPersistedScreenPath(this.$stateParams.planningSessionId, this.determineScreenName());

  private getPersistedFilterSettings = (): OrFiltersGroup[] => {
    return getPersistedScreenSettings(this.screenFiltersPath(), "filters") || [];
  };

  private getPersistedSortingSettings = (): IGridSortingRule[] => {
    return getPersistedScreenSettings(this.screenSortingPath(), "sorting") || [];
  };

  private toValidPersistedSortingSettings = (sorting: IGridSortingRule[]): IGridSortingRule[] => {
    return sorting.filter((sortingRule) => sortingRule.fieldName in this.$scope.sortingDefinitions);
  };

  private toValidPersistedFilterSettings = (persistedFilters: OrFiltersGroup[]): OrFiltersGroup[] => {
    const createSessionFilter = (): IArrayGridFilter => ({
      fieldName: "sessionId",
      dataType: "array",
      operator: "isOneOf",
      value: [this.$stateParams.planningSessionId],
    });

    return persistedFilters.map((filtersGroup) => {
      // if no session filter is persisted, add session filter at first place
      const sessionFilterIndex = filtersGroup.findIndex((filter) => filter.fieldName === "sessionId");
      if (sessionFilterIndex < 0) {
        filtersGroup = [createSessionFilter(), ...filtersGroup];
      } else if (this.$scope.viewModes.nested) {
        // in nested view, we do not want to use the persisted sessionId filter since it is readonly
        const newSessionFilter = createSessionFilter();
        const persistedSessionFilter = filtersGroup[sessionFilterIndex];
        this.replacedPersistedFiltersMap.set(newSessionFilter, persistedSessionFilter);
        filtersGroup[sessionFilterIndex] = newSessionFilter;
      }

      return filtersGroup;
    });
  };

  private initSortingSettings = (): void => {
    this.$scope.sortingDefinitions = toGridDefinitionMap([
      { fieldName: "assignee.name", displayName: localize("owner_cap"), trackingName: "OKR owner" },
      { fieldName: "name", displayName: localize("objective_name"), trackingName: "OKR name" },
      { fieldName: "attainment", displayName: localize("progress_cap"), trackingName: "OKR progress" },
      { fieldName: "designScore.totalPoints", displayName: localize("okr_design_score"), trackingName: "OKR-design score" },
    ]);

    if (!this.shouldInitAlignmentFiltersUsingPersistedSettings()) {
      this.initSortingWithValue(this.sharedFilterSettings.sorting);
      return;
    }

    const persistedSorting = this.toValidPersistedSortingSettings(this.getPersistedSortingSettings());
    this.initSortingWithValue(persistedSorting);
  };

  private generateDefaultSortingSettings = (): IGridSortingRule[] => [{ fieldName: "name", reversed: false }];

  private initSortingWithValue = (sortingSettings: IGridSortingRule[]): void => {
    if (sortingSettings.length) {
      this.$scope.sortingRules = sortingSettings;
    } else {
      this.$scope.sortingRules = this.generateDefaultSortingSettings();
    }
  };

  private initFilterSettings = (): void => {
    this.$scope.filterDefinitions = goalsFilterDefinitions({
      isNestedView: this.$scope.viewModes.nested,
      customFields: this.$scope.customFields,
      planningSessionId: this.$stateParams.planningSessionId,
    });

    if (!this.shouldInitAlignmentFiltersUsingPersistedSettings()) {
      this.initFiltersWithValue([this.sharedFilterSettings.filters]);
      return;
    }

    const persistedFilters = this.toValidPersistedFilterSettings(this.getPersistedFilterSettings());

    if (this.$stateParams.locateId && !arrayFilterIncludes(persistedFilters, "sessionId", this.$stateParams.planningSessionId)) {
      const defaultFilters = this.generateDefaultFilters();
      this.initFiltersWithValue(defaultFilters);
      return;
    }

    this.initFiltersWithValue(persistedFilters);
  };

  private initFiltersWithValue = (filterSettings: OrFiltersGroup[]): void => {
    if (filterSettings.length) {
      this.$scope.filters = filterSettings;
    } else {
      this.$scope.filters = this.generateDefaultFilters();
    }

    if (this.$scope.isOkrFiltersImprovementEnabled && this.$scope.filtersUpdated) {
      this.$scope.filtersUpdated(this.$scope.filters);
    }
  };

  private generateDefaultFilters = (): OrFiltersGroup[] => {
    if (!this.shouldInitAlignmentFiltersUsingPersistedSettings()) {
      return generateDefaultFiltersWithoutSession();
    }

    return generateDefaultFilters(this.$stateParams.planningSessionId);
  };

  private getSharedFilterSettings = (filterId: string): IPromise<ShareableFilter> => {
    const shareableFilterDefer = this.$q.defer<ShareableFilter>();

    this.shareableFiltersFacade
      .getShareableFilterById$(filterId)
      .pipe(take(1))
      .subscribe({
        next: (shareableFilter) => {
          shareableFilterDefer.resolve(shareableFilter);
        },
        error: () => shareableFilterDefer.resolve({ id: "", name: "", filters: [], sorting: [] }),
      });

    return shareableFilterDefer.promise;
  };

  private initUnifiedFilters = (): IPromise<void> => {
    if (this.shouldInitAlignmentFiltersUsingPersistedSettings()) {
      this.initSortingSettings();
      this.initFilterSettings();
      return this.$q.resolve();
    }

    return this.getSharedFilterSettings(this.$stateParams.filterId || this.$stateParams.okrViewId).then((sharedFilterSettings) => {
      this.sharedFilterSettings = sharedFilterSettings;
      this.initOkrViewEventSubscriptions();

      this.initSortingSettings();
      this.initFilterSettings();
    });
  };

  private initOkrViewEventSubscriptions = (): void => {
    if (!this.$stateParams.okrViewId || this.okrViewEventsManager) return;

    this.okrViewEventsManager = new OkrViewEventsManager(this.$stateParams.okrViewId);
    this.okrViewEventsManager
      .okrViewAccessChanged$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((access) => {
        this.sharedFilterSettings.access = access;
      });
  };

  private shouldInitAlignmentFiltersUsingPersistedSettings = (): boolean => {
    return !this.shouldLoadFiltersFromTransientFilter() && !this.shouldLoadFiltersFromPermanentFilter();
  };

  private shouldLoadFiltersFromTransientFilter = (): boolean => {
    // transient filters are used only for shareable links with applied filters
    return this.$scope.isShareableLinksWithAppliedFiltersFeatureEnabled && !!this.$stateParams.filterId;
  };

  private shouldLoadFiltersFromPermanentFilter = (): boolean => {
    // permanent filters are used only for OKR views
    return this.$scope.isOkrViewsFeatureEnabled && !!this.$stateParams.okrViewId;
  };

  private filtersChange = (change: IGridFiltersChange): void => {
    this.$rootScope.traceAction("change_filters", () => {
      if (!change.initial) {
        this.subsequentFiltersChange();
      }

      const args = this.constructAlignmentViewArgsFromFilters();
      const isAlignmentLoaderResolved = this.alignmentLoader.isResolved;

      const stateParams = { ...this.$stateParams };
      if (!isAlignmentLoaderResolved) {
        this.alignmentLoader.resolve(args);
        // removing sharedId from state params as it won't be needed anymore
        stateParams.sharedId = undefined;
        stateParams.sharedParentId = undefined;
        this.$state.go(".", stateParams, { notify: false });
      } else {
        this.$rootScope.$broadcast("alignment.unifiedFiltersChanged", args);
      }

      if (this.$scope.isOkrFiltersImprovementEnabled && this.$scope.filtersUpdated) {
        this.$scope.filtersUpdated(this.$scope.filters);
      }
    });
  };

  private subsequentFiltersChange = (): void => {
    this.$scope.hasFilterChanged = true;

    let filtersToPersist = this.$scope.filters;
    if (this.$scope.viewModes.nested) {
      // If there is a persisted session filter, we have to keep it, since the user might
      // need it on the grid / tree screens
      filtersToPersist = this.$scope.filters.map((filtersGroup) => {
        return filtersGroup
          .filter((filter) => filter.fieldName !== "sessionId" || (filter.fieldName === "sessionId" && this.replacedPersistedFiltersMap.has(filter)))
          .map((filter) => {
            if (filter.fieldName === "sessionId") {
              return this.replacedPersistedFiltersMap.get(filter);
            }
            return filter;
          });
      });
    }

    if (this.shouldInitAlignmentFiltersUsingPersistedSettings()) {
      setPersistedScreenSettings(this.screenFiltersPath(), "filters", filtersToPersist);
    }
  };

  private sortingChange = (): void => {
    this.$rootScope.traceAction("change_sorting", () => {
      if (this.shouldInitAlignmentFiltersUsingPersistedSettings()) {
        setPersistedScreenSettings(this.screenSortingPath(), "sorting", this.$scope.sortingRules);
      }

      const broadcastArgs: Record<"order", IGridSortingRule[]> = {
        order: this.$scope.sortingRules,
      };
      this.$scope.$broadcast("alignment.orderChanged", broadcastArgs);
    });
  };

  private determineScreenName = (): "alignment" | "nested" => {
    return this.$scope.viewModes.nested ? nestedScreenName : alignmentScreenName;
  };

  private handleShareableLinkCopiedFromSubNavigation = (event, item: INavItem): void => {
    const allShareableFiltersDisabled = !this.$scope.isShareableLinksWithAppliedFiltersFeatureEnabled && !this.$scope.isOkrViewsFeatureEnabled;
    const typeNotSupported = item.uxcType !== "session" && item.uxcType !== "filter";
    if (typeNotSupported || allShareableFiltersDisabled) return;

    this.copyShareableLinkWithAppliedFilters();
  };

  private copyShareableLinkWithAppliedFilters = (): void => {
    const currentState = this.$uiRouterGlobals.current.name;
    const isTreeLoadedFromOkrView = currentState.startsWith(BASE_OKR_VIEW_STATE);

    if (isTreeLoadedFromOkrView) {
      this.copyShareableLinkWithAppliedPermanentFilters();
      return;
    }

    this.copyShareableLinkWithAppliedTransientFilters();
  };

  private copyShareableLinkWithAppliedPermanentFilters = (): void => {
    if (determineAccessType(this.sharedFilterSettings.access) === SimplePermissionsAccessType.private) {
      this.shareableFiltersFacade.notifyFilterAccessRestricted("Edit who has access to make this OKR view visible to others");
      this.okrViewEventsManager.notifyOkrViewPrivateAccessChangeRequested();

      return;
    }

    const currentState = this.$uiRouterGlobals.current.name;
    this.shareableFiltersFacade.copyShareableOkrViewLink(currentState, { okrViewIs: this.$stateParams.okrViewId });
  };

  private copyShareableLinkWithAppliedTransientFilters = (): void => {
    const currentState = this.$uiRouterGlobals.current.name;
    const shareableFilter: ShareableFilterTransientPostDTO = {
      filters: this.$scope.filters[0],
      sorting: this.$scope.sortingRules,
      transient: true,
    };

    this.shareableFiltersFacade.copyShareableLinkWithAppliedFilterIdQuery(shareableFilter, currentState, { planningSessionId: this.$stateParams.planningSessionId });
  };

  private resetPersistedExpandedItems = (): void => {
    if (this.$scope.isTreeLoadedFromOkrView) {
      resetPersistedExpandedItems({ entityId: this.$stateParams.okrViewId, screenPath: expandedGoalsOkrViewScreenPath(this.$stateParams.okrViewId) });
    }
  };
}
